/**
 * ContactCategoriesCard — Admin Diagnostics (2026-02).
 *
 * READ-ONLY. Hits `GET /api/admin/diagnostics/contact-categories` and
 * renders: a summary line + one table per discovered category-shaped
 * field with distinct-value counts. Same visual language as the
 * Contact Duplicates card. No Apply, Assign, Delete, or Merge controls.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Tags, Copy, RefreshCw, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatBox({ label, value, tone = 'slate', testId }) {
  const map = {
    slate: 'bg-slate-100 text-slate-900 border-slate-300',
    amber: 'bg-amber-100 text-amber-900 border-amber-300',
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    indigo: 'bg-indigo-100 text-indigo-900 border-indigo-300',
  };
  return (
    <div className={`p-3 rounded-lg border ${map[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-mono font-semibold">{value}</div>
    </div>
  );
}

function FieldSection({ field, i, testId }) {
  return (
    <details
      className="border rounded bg-white"
      open={i < 3}
      data-testid={`${testId}-field-${i}`}
    >
      <summary className="p-2 cursor-pointer flex items-center gap-2 flex-wrap">
        <Badge className="bg-slate-800 text-white font-mono text-[11px]">{field.observed_shape}</Badge>
        <code className="font-semibold text-slate-900">{field.field}</code>
        <span className="text-[11px] text-slate-600">
          contacts with value: <strong className="text-slate-900">{field.contacts_with_field_populated}</strong>
          {' · '}distinct values: <strong className="text-slate-900">{field.distinct_value_count}</strong>
        </span>
      </summary>
      <table className="w-full text-xs border-t">
        <thead className="bg-slate-100">
          <tr className="text-left">
            <th className="p-1.5">Value</th>
            <th className="p-1.5 text-right">Contact count</th>
          </tr>
        </thead>
        <tbody data-testid={`${testId}-field-${i}-tbody`}>
          {field.values.length === 0 ? (
            <tr><td colSpan={2} className="p-2 italic text-slate-500 text-center">(no distinct values)</td></tr>
          ) : field.values.map((row, ri) => (
            <tr key={`${row.value}-${ri}`} className="border-t hover:bg-slate-50" data-testid={`${testId}-field-${i}-row-${ri}`}>
              <td className="p-1.5 font-mono break-all">{row.value}</td>
              <td className="p-1.5 text-right font-mono font-semibold">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

export default function ContactCategoriesCard({ isSuperAdmin }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/contact-categories`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const copyJSON = () => {
    if (!data) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Copied categories report as JSON');
    } catch { /* ignore */ }
  };

  if (!isSuperAdmin) return null;
  const s = data?.summary;

  return (
    <div
      className="p-5 rounded-xl border border-slate-300 bg-white mb-6"
      data-testid="contact-categories-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <Tags className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold">Contact Categories</h3>
          <p className="text-sm text-slate-700">
            Read-only report. Inspects the <code>contacts</code> collection for Keystone
            Creative and enumerates every category-shaped field actually present on the
            documents (<code>category</code>, <code>type</code>, <code>tags</code>,
            <code>group</code>, <code>labels</code>, etc. — whatever is really there), with
            distinct-value counts. No Assign, Merge, Delete, or Apply controls exist on
            this card.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={run} disabled={busy} data-testid="contact-categories-run-btn">
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning…</>
            ) : data ? (
              <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>
            ) : (
              <><Tags className="w-4 h-4 mr-2" /> Run Contact Categories</>
            )}
          </Button>
          {data && (
            <Button variant="ghost" size="sm" onClick={copyJSON} data-testid="contact-categories-copy-btn">
              <Copy className="w-4 h-4 mr-1" /> Copy JSON
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-2" data-testid="contact-categories-error">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">Report failed</div>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          <div className="p-3 rounded-lg border bg-slate-50 border-slate-200" data-testid="contact-categories-summary">
            <div className="text-sm text-slate-900 mb-2" data-testid="contact-categories-summary-sentence">
              Scanned <strong>{s.total_contacts_scanned}</strong> Keystone contacts.
              Discovered <strong>{s.discovered_fields_count}</strong> category-shaped field(s).
              <strong> {s.uncategorized_contacts}</strong> contact(s) have no category or tag value at all.
            </div>
            <div className="grid grid-cols-3 gap-2">
              <StatBox label="Total contacts scanned" value={s.total_contacts_scanned} tone="slate" testId="cat-stat-scanned" />
              <StatBox label="Category-shaped fields" value={s.discovered_fields_count} tone={s.discovered_fields_count ? 'indigo' : 'emerald'} testId="cat-stat-fields" />
              <StatBox label="Uncategorized contacts" value={s.uncategorized_contacts} tone={s.uncategorized_contacts ? 'amber' : 'emerald'} testId="cat-stat-uncategorized" />
            </div>
          </div>

          <section data-testid="contact-categories-section-fields" className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-800">
              Discovered fields (each shows distinct values + contact counts)
            </h4>
            {data.fields.length === 0 ? (
              <div className="text-xs italic px-2 py-2 border rounded bg-emerald-50 border-emerald-200 text-emerald-800">
                no category-shaped fields found on any Keystone contact
              </div>
            ) : (
              data.fields.map((f, i) => (
                <FieldSection key={f.field} field={f} i={i} testId="contact-categories-field" />
              ))
            )}
          </section>

          <section data-testid="contact-categories-notes">
            <details>
              <summary className="text-xs text-slate-500 cursor-pointer">Detection rules &amp; guarantees</summary>
              <ul className="mt-1 text-xs text-slate-600 list-disc pl-5 space-y-1">
                {(data.notes || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </details>
          </section>
        </div>
      )}
    </div>
  );
}
